import { useCallback, useMemo, useState } from "react"
import { orderBy } from "lodash"

import AssetInspection, { AssetInspectionType } from "models/AssetInspection"
import AssetService, { IGetAssetInspectionsParams } from "services/AssetService"
import toastMessages from "stores/toastMessages"

interface IUseAssetInspectionsParams extends IGetAssetInspectionsParams {
    groupBy?: keyof AssetInspection
}

export default function useAssetInspections({
    assetId,
    reservationId,
    shippingOrderId,
    groupBy,
}: IUseAssetInspectionsParams) {
    const [inspections, setInspections] = useState<AssetInspection[] | null>(null)

    const fetchAssetInspections = useCallback(async () => {
        try {
            const newInspections = await AssetService.getAssetInspections({
                assetId,
                reservationId,
                shippingOrderId,
            })
            setInspections(newInspections)
        } catch (e) {
            console.error("Could not fetch asset inspections", e)
            toastMessages.showSystemError()
        }
    }, [assetId, reservationId, shippingOrderId])

    const inspectionsGrouped = useMemo(() => {
        if (!groupBy || !inspections) {
            return null
        }

        const grouped = new Map<AssetInspection[typeof groupBy], AssetInspection[]>()
        inspections.forEach(inspection => {
            const group = inspection[groupBy]
            if (!grouped.has(group)) {
                grouped.set(group, [])
            }
            grouped.get(group)?.push(inspection)
        })
        return grouped
    }, [groupBy, inspections])

    const getLatestInspections = useCallback((insp: AssetInspection[]) => {
        const inspectionsSorted = orderBy(insp, i => i.inspectionDate.unix(), "desc")
        const latestDeliveryInspection = inspectionsSorted.find(i => i.inspectionType === AssetInspectionType.delivery)
        const latestReturnInspection = inspectionsSorted.find(i => i.inspectionType === AssetInspectionType.return)

        return {
            deliveryInspection: latestDeliveryInspection,
            returnInspection: latestReturnInspection,
            latestInspection: inspectionsSorted[0],
        }
    }, [])

    const globalLatestAssetInspection = useMemo(() => {
        if (!inspections) return null
        return getLatestInspections(inspections).latestInspection
    }, [inspections, getLatestInspections])

    return {
        assetInspections: inspections,
        assetInspectionsGrouped: inspectionsGrouped,
        fetchAssetInspections,
        getLatestInspections,
        globalLatestAssetInspection,
    }
}
